import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from './api-client';

// Get all vehicles with pagination
export const useGetVehicles = (options = {}) => {
  const { skip = 0, limit = 10, ...otherOptions } = options;

  return useQuery(
    ['vehicles', { skip, limit, ...otherOptions }],
    async () => {
      const searchParams = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
        ...otherOptions,
      });

      const response = await client(`vehicles?${searchParams.toString()}`);

      if (response?.error) {
        throw new Error(response.error);
      }

      return response;
    },
    {
      keepPreviousData: true,
    }
  );
};

// Get single vehicle by ID
export const useGetVehicle = vehicleId => {
  return useQuery(
    ['vehicles', vehicleId],
    async () => {
      const response = await client(`read_vehicle/${vehicleId}`);

      if (response?.error) {
        throw new Error(response.error);
      }

      return response;
    },
    {
      enabled: !!vehicleId,
      retry: 3,
    }
  );
};

// Create new vehicle
export const useCreateVehicle = () => {
  const queryClient = useQueryClient();

  const createVehicle = async vehicleData => {
    const response = await client('create_vehicle', {
      method: 'POST',
      data: vehicleData,
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return response;
  };

  return useMutation(createVehicle, {
    onSuccess: data => {
      console.log('✅ Vehicle created successfully:', data);

      // Invalidate vehicles list to refetch updated data
      queryClient.invalidateQueries(['vehicles']);
    },
    onError: error => {
      console.error('❌ Failed to create vehicle:', error.message);
    },
  });
};

// Create vehicle for non-logged-in users via email
export const useCreateVehicleWithEmail = () => {
  return useMutation(async requestData => {
    const response = await client('create-vehicle-with-email', {
      method: 'POST',
      data: requestData,
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return response;
  }, {
    onSuccess: data => {
      console.log('✅ Vehicle request created for email:', data);
    },
    onError: error => {
      console.error('❌ Failed to create vehicle with email:', error.message);
    },
  });
};

// Update vehicle
export const useUpdateVehicle = () => {
  const queryClient = useQueryClient();

  const updateVehicle = async ({ vehicleId, updateData }) => {
    const response = await client(`update_vehicle/${vehicleId}`, {
      method: 'PUT',
      data: updateData,
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return response;
  };

  return useMutation(updateVehicle, {
    onSuccess: (data, variables) => {
      console.log('✅ Vehicle updated successfully:', data);

      // Update the specific vehicle in cache
      queryClient.invalidateQueries(['vehicles', variables.vehicleId]);

      // Update vehicles list
      queryClient.invalidateQueries(['vehicles']);
    },
    onError: error => {
      console.error('❌ Failed to update vehicle:', error.message);
    },
  });
};

// Delete vehicle
export const useDeleteVehicle = () => {
  const queryClient = useQueryClient();

  const deleteVehicle = async vehicleId => {
    const response = await client(`delete_vehicle/${vehicleId}`, {
      method: 'DELETE',
    });

    if (response?.error) {
      throw new Error(response.error);
    }

    return 'car deleted';
  };

  return useMutation(deleteVehicle, {
    onMutate: async vehicleId => {
      console.log('🔄 Starting optimistic update for vehicle ID:', vehicleId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries(['vehicles', 'user']);

      // Snapshot the previous value
      const previousVehicles = queryClient.getQueriesData(['vehicles', 'user']);
      console.log('📸 Snapshot of previous vehicles:', previousVehicles);

      // Optimistically update to the new value
      // Try to update all queries that start with ['vehicles', 'user']
      const queries = queryClient.getQueriesData(['vehicles', 'user']);
      console.log('🔄 Found queries to update:', queries);

      queries.forEach(([queryKey, queryData]) => {
        if (queryData && Array.isArray(queryData)) {
          const filteredData = queryData.filter(
            vehicle => vehicle.id !== vehicleId
          );
          console.log(`🔄 Updating query ${queryKey}:`, {
            old: queryData,
            new: filteredData,
          });
          queryClient.setQueryData(queryKey, filteredData);
        }
      });

      // Return a context object with the snapshotted value
      return { previousVehicles };
    },
    onError: (err, vehicleId, context) => {
      console.error('❌ Failed to delete vehicle:', err.message);

      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousVehicles) {
        context.previousVehicles.forEach(([queryKey, queryData]) => {
          queryClient.setQueryData(queryKey, queryData);
        });
      }
    },
    onSettled: (data, error, vehicleId) => {
      console.log('🔄 Delete operation settled, invalidating cache');
      console.log('🔄 Final data:', data);
      console.log('🔄 Final error:', error);

      // Always refetch after error or success to ensure cache consistency
      queryClient.invalidateQueries(['vehicles', 'user']);

      // Log the final cache state
      const finalCache = queryClient.getQueriesData(['vehicles', 'user']);
      console.log('🔄 Final cache state:', finalCache);
    },
  });
};

// Get vehicles by user ID
export const useGetUserVehicles = userId => {
  return useQuery(
    ['vehicles', 'user', userId],
    async () => {
      console.log('🔄 Fetching vehicles for user:', userId);
      const response = await client(`read_user_vehicles?user_id=${userId}`);

      if (response?.error) {
        throw new Error(response.error);
      }

      console.log('🔄 Vehicles API response:', response);
      // API now returns conversation_id directly, no mapping needed
      return response;
    },
    {
      enabled: !!userId,
      retry: 3,
      // Add staleTime to prevent unnecessary refetches
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: data => {
        console.log('🔄 Vehicles query success:', data);
      },
      onError: error => {
        console.error('🔄 Vehicles query error:', error);
      },
    }
  );
};

// Get vehicle score and valuation
export const useGetVehicleScoreAndValuation = vehicleId => {
  return useQuery(
    ['vehicles', 'score-valuation', vehicleId],
    async () => {
      console.log(
        '🔄 Fetching vehicle score and valuation for vehicle:',
        vehicleId
      );
      const response = await client(`vehicle-score-and-valuation/${vehicleId}`);

      if (response?.error) {
        throw new Error(response.error);
      }

      console.log('🔄 Vehicle score and valuation API response:', response);
      return response;
    },
    {
      enabled: !!vehicleId,
      retry: 3,
      staleTime: 10 * 60 * 1000, // 10 minutes - score/valuation data doesn't change frequently
      onSuccess: data => {
        console.log('✅ Vehicle score and valuation query success:', data);
      },
      onError: error => {
        console.error('❌ Vehicle score and valuation query error:', error);
      },
    }
  );
};
